import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import {
  CheckCircle2,
  FileText,
  Clock,
  Banknote,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
} from "lucide-react";

const KobutsushoGuidePage: React.FC = () => {
  const requiredDocs = [
    { name: "許可申請書", source: "警察署 or ダウンロード", note: "正副2通" },
    { name: "略歴書", source: "自分で作成", note: "過去5年間の経歴" },
    { name: "誓約書", source: "警察署 or ダウンロード", note: "欠格事由に該当しないことの誓約" },
    { name: "住民票", source: "市区町村役場", note: "本籍地記載、マイナンバー記載なし" },
    { name: "身分証明書", source: "本籍地の市区町村役場", note: "破産者でないことの証明" },
    { name: "URLの届出書", source: "自分で作成", note: "ネット販売する場合のみ" },
  ];

  const kobutsuCategories = [
    { category: "美術品類", example: "絵画、彫刻" },
    { category: "衣類", example: "洋服、着物" },
    { category: "時計・宝飾品類", example: "腕時計、アクセサリー" },
    { category: "自動車", example: "中古車" },
    { category: "写真機類", example: "カメラ、レンズ" },
    { category: "事務機器類", example: "パソコン、プリンター" },
    { category: "機械工具類", example: "工具、家電" },
    { category: "道具類", example: "トレーディングカード、CD、DVD、ゲーム", highlight: true },
    { category: "皮革・ゴム製品類", example: "バッグ、靴" },
    { category: "書籍", example: "古本" },
    { category: "金券類", example: "商品券、チケット" },
  ];

  const faqItems = [
    {
      question: "自宅を営業所にできますか？",
      answer: "はい、可能です。ただし、賃貸の場合は大家さんの許可が必要な場合があります。",
    },
    {
      question: "副業でも古物商許可証は必要ですか？",
      answer: "はい、継続的に利益を得る目的で中古品を売買する場合は、副業であっても必要です。",
    },
    {
      question: "ネット販売だけでも必要ですか？",
      answer: "はい、必要です。ネット販売の場合は「URLの届出書」も追加で提出します。",
    },
    {
      question: "許可証がないとどうなりますか？",
      answer: "無許可営業は「3年以下の懲役または100万円以下の罰金」の対象となります。",
    },
    {
      question: "行政書士に依頼した方がいいですか？",
      answer: "書類作成が不安な方は行政書士への依頼も選択肢です。費用は3〜5万円程度が相場です。",
    },
  ];

  return (
    <BlogLayout
      title="古物商許可証の取り方完全ガイド"
      description="オリパ販売・カード販売を始めるために必要な古物商許可証。取得の流れ・必要書類・費用まで詳しく解説します。"
      category="ガイド"
      publishedAt="2025年12月1日"
      breadcrumbs={[{ label: "古物商許可証の取り方" }]}
    >
      <Helmet>
        <title>【2025年最新】古物商許可証の取り方完全ガイド | JoyBase</title>
        <meta
          name="description"
          content="オリパ販売・カード販売に必要な古物商許可証の取得方法を詳しく解説。必要書類、申請手数料、審査期間まで網羅。トレカショップ開業を検討中の方必見。"
        />
        <meta
          name="keywords"
          content="古物商許可証,取り方,オリパ販売,カード販売,トレカショップ,開業,申請方法"
        />
        <meta property="og:title" content="【2025年最新】古物商許可証の取り方完全ガイド | JoyBase" />
        <meta
          property="og:description"
          content="オリパ販売・カード販売に必要な古物商許可証の取得方法を詳しく解説。"
        />
        <meta property="og:url" content="https://joybase.jp/blog/kobutsusho-guide" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://joybase.jp/blog/kobutsusho-guide" />
      </Helmet>

      {/* 古物商許可証とは */}
      <section className="mb-12">
        <h2>古物商許可証とは</h2>
        <p>
          古物商許可証は、<strong>古物営業法</strong>に基づき、中古品の売買を業として行うために必要な許可です。
          トレーディングカードの売買やオリパ販売を行う場合、ほとんどのケースで取得が必要になります。
        </p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 my-6">
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2 text-base">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            トレーディングカードは「道具類」に分類
          </h3>
          <p className="text-gray-600 text-sm m-0">
            古物営業法では、トレーディングカードは「道具類」に分類され、古物に該当します。
            中古カードを仕入れて販売する場合は、古物商許可証が必要です。
          </p>
        </div>

        <h3>古物の13品目</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
          {kobutsuCategories.map((item, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                item.highlight ? "bg-yellow-100 border-2 border-yellow-400" : "bg-gray-50"
              }`}
            >
              <CheckCircle2
                className={`w-5 h-5 flex-shrink-0 ${
                  item.highlight ? "text-yellow-600" : "text-gray-400"
                }`}
              />
              <div>
                <span className={`font-medium ${item.highlight ? "text-yellow-800" : "text-gray-900"}`}>
                  {item.category}
                </span>
                <span className="text-gray-500 text-sm ml-2">{item.example}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* オリパ販売に必要？ */}
      <section className="mb-12">
        <h2>オリパ販売に古物商許可証は必要？</h2>

        <div className="grid md:grid-cols-2 gap-6 my-6 not-prose">
          <div className="bg-red-50 rounded-lg p-6">
            <h3 className="font-bold text-red-700 mb-4">必要なケース</h3>
            <ul className="space-y-3">
              {[
                "中古カードを仕入れてオリパに封入する",
                "中古カードを買い取って販売する",
                "ユーザーから買い取ったカードを使う",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-0.5">●</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-green-50 rounded-lg p-6">
            <h3 className="font-bold text-green-700 mb-4">不要なケース</h3>
            <ul className="space-y-3">
              {[
                "新品のみを仕入れて販売",
                "自分のコレクションを売却するだけ（継続的な営業でない場合）",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-700">
                  <span className="text-green-500 mt-0.5">●</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-blue-800 text-sm m-0">
            <strong>実際には</strong>
            オリパ販売を行う場合、中古カードを一切使わないのは現実的に難しいため、
            <strong>古物商許可証の取得を強く推奨</strong>します。
          </p>
        </div>
      </section>

      {/* 取得の流れ */}
      <section className="mb-12">
        <h2>取得の流れ</h2>

        <div className="space-y-6 not-prose">
          {[
            {
              step: 1,
              title: "管轄の警察署を確認",
              desc: "営業所（自宅でも可）の所在地を管轄する警察署の「生活安全課」が申請窓口です。",
            },
            {
              step: 2,
              title: "必要書類の準備",
              desc: "後述の書類を揃えます（2〜3週間かかる場合あり）。",
            },
            {
              step: 3,
              title: "警察署で申請",
              desc: "必要書類と申請手数料を持参し、窓口で申請します。",
            },
            {
              step: 4,
              title: "審査",
              desc: "警察による審査が行われます（約40日）。",
            },
            {
              step: 5,
              title: "許可証交付",
              desc: "審査通過後、警察署で許可証を受け取ります。",
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-600 text-white font-bold rounded-full flex items-center justify-center">
                  {item.step}
                </div>
              </div>
              <div className="flex-1 pb-6 border-b border-gray-200 last:border-0">
                <h4 className="font-bold text-gray-900">{item.title}</h4>
                <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 必要書類 */}
      <section className="mb-12">
        <h2 className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-500" />
          必要書類（個人申請の場合）
        </h2>

        <div className="overflow-x-auto not-prose">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  書類
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  取得先
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 hidden sm:table-cell">
                  備考
                </th>
              </tr>
            </thead>
            <tbody>
              {requiredDocs.map((doc, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                    {doc.name}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-gray-600">{doc.source}</td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                    {doc.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 費用と審査期間 */}
      <section className="mb-12">
        <h2>費用と審査期間</h2>

        <div className="grid md:grid-cols-2 gap-6 my-6 not-prose">
          <div className="bg-yellow-50 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Banknote className="w-8 h-8 text-yellow-600" />
              <h3 className="text-lg font-bold text-gray-900">申請手数料</h3>
            </div>
            <p className="text-3xl font-bold text-yellow-600">19,000円</p>
            <p className="text-sm text-gray-600 mt-2">
              許可証の受取時に支払い（収入証紙）
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-8 h-8 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">審査期間</h3>
            </div>
            <p className="text-3xl font-bold text-blue-600">約40日</p>
            <p className="text-sm text-gray-600 mt-2">
              書類に不備がなければ1ヶ月〜1ヶ月半程度
            </p>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="font-bold text-green-800 mb-2">審査中にできること</h4>
          <ul className="text-green-700 text-sm space-y-1 m-0 list-none p-0">
            <li>✓ 営業所の準備</li>
            <li>✓ 商品の仕入れ準備</li>
            <li>✓ JoyBaseでのサイト設定・Stripe審査</li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2>よくある質問</h2>

        <div className="space-y-4 not-prose">
          {faqItems.map((item, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Q: {item.question}</h3>
              <p className="text-gray-600 text-sm m-0">A: {item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 参考リンク */}
      <section>
        <h2>参考リンク</h2>
        <a
          href="https://www.keishicho.metro.tokyo.lg.jp/tetsuzuki/kobutsu/kaisetsu/kobutsu.html"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          警視庁 古物商許可申請
        </a>
      </section>
    </BlogLayout>
  );
};

export default KobutsushoGuidePage;
